import React from "react";
import { business, navLinks, services, whatsappLink } from "../data/site";
import { ArrowUpRight, Instagram, WhatsApp } from "./Icons";
import Reveal from "./Reveal";
import { cx, eyebrow, inner } from "../lib/ui";

const linkClass =
  "group/link relative inline-block max-w-full [overflow-wrap:anywhere] transition-colors duration-300 hover:text-paper";

const underline =
  "pointer-events-none absolute -bottom-0.5 left-0 h-px w-full origin-right scale-x-0 bg-current transition-transform duration-[400ms] ease-out group-hover/link:origin-left group-hover/link:scale-x-100";

const socialClass =
  "inline-flex items-center gap-2.5 rounded-full border border-white/15 px-5 py-3 text-[11.5px] font-medium uppercase tracking-[0.16em] transition-colors duration-[400ms] ease-out hover:border-paper hover:bg-paper hover:text-ink";

export default function Footer() {
  const year = new Date().getFullYear();

  const toTop = (event) => {
    event.preventDefault();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  const columns = [
    {
      title: "Reach",
      items: [
        { label: business.phoneDisplay, href: `tel:${business.phoneDial}` },
        { label: business.phoneDisplayAlt, href: `tel:${business.phoneDialAlt}` },
        { label: business.email, href: `mailto:${business.email}` },
        { label: business.website, href: business.websiteUrl, external: true },
      ],
    },
    {
      title: "Sections",
      items: navLinks.map((link) => ({ label: link.label, href: `#${link.id}` })),
    },
    {
      title: "Verticals",
      items: services.map((service) => ({ label: service.title, href: "#services" })),
    },
  ];

  return (
    <footer className="border-t border-white/15 bg-ink pb-7 pt-[clamp(52px,7vw,96px)] text-paper">
      <div className={cx(inner, "px-gutter")}>
        <Reveal className="grid gap-4 border-b border-white/15 pb-[clamp(34px,5vw,60px)]">
          <img
            src="/images/logo-white.png"
            alt={business.name}
            width="640"
            height="164"
            loading="lazy"
            className="h-auto w-[min(560px,82%)]"
          />
          <p className="font-display text-[clamp(17px,2vw,24px)] text-ash-300">
            {business.tagline} <span className="italic">— since {business.since}</span>
          </p>
        </Reveal>

        <div className="grid grid-cols-2 gap-[clamp(28px,3.4vw,52px)] py-[clamp(34px,4.6vw,62px)] md:grid-cols-[1.3fr_repeat(3,1fr)]">
          <Reveal className="grid min-w-0 content-start gap-3">
            <p className={eyebrow}>Visit</p>
            <address className="grid gap-[3px] text-[13.5px] not-italic leading-[1.6] text-ash-200">
              {business.address.lines.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </address>
            <p className="mt-1 text-xs tracking-[0.1em] text-ash-400">{business.hours}</p>
          </Reveal>

          {columns.map((column, ci) => (
            <Reveal key={column.title} className="grid min-w-0 content-start gap-3" delay={(ci + 1) * 70}>
              <p className={eyebrow}>{column.title}</p>
              <ul className="grid min-w-0 gap-2 text-[13.5px] text-ash-200">
                {column.items.map((item) => (
                  <li key={item.label} className="min-w-0">
                    <a
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noreferrer noopener" : undefined}
                      className={linkClass}
                    >
                      {item.label}
                      <span className={underline} />
                    </a>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>

        <div className="flex flex-wrap gap-2.5 border-b border-white/15 pb-[clamp(28px,4vw,48px)]">
          <a
            href={business.instagram}
            target="_blank"
            rel="noreferrer noopener"
            className={socialClass}
          >
            <Instagram width="17" height="17" />
            <span>{business.instagramHandle}</span>
            <ArrowUpRight width="14" height="14" />
          </a>
          <a href={whatsappLink} target="_blank" rel="noreferrer noopener" className={socialClass}>
            <WhatsApp width="17" height="17" />
            <span>WhatsApp</span>
            <ArrowUpRight width="14" height="14" />
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3.5 pt-[22px] text-[11.5px] tracking-[0.06em] text-ash-400">
          <p>
            © {year} {business.name}. All rights reserved.
          </p>
          <button
            type="button"
            onClick={toTop}
            className="group inline-flex items-center gap-2.5 text-[11px] uppercase tracking-[0.18em] text-ash-300 transition-colors duration-300 hover:text-paper"
          >
            Back to top
            <i className="grid h-7 w-7 place-items-center rounded-full border border-white/15 transition-[transform,background-color,color,border-color] duration-[450ms] ease-out group-hover:-translate-y-[3px] group-hover:border-paper group-hover:bg-paper group-hover:text-ink">
              <ArrowUpRight width="13" height="13" />
            </i>
          </button>
        </div>
      </div>
    </footer>
  );
}
